import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HandHeart, Plus, Coins, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export default function HelpBoard() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", kind: "request", amir_amount: 0 });
  const [tab, setTab] = useState("all");

  const load = () => api.get("/help-requests").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.description.trim()) return toast.error("Please fill in the details");
    try {
      await api.post("/help-requests", { ...form, amir_amount: Number(form.amir_amount) || 0 });
      toast.success("Posted to the help board 💛");
      setOpen(false);
      setForm({ title: "", description: "", kind: "request", amir_amount: 0 });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const fulfill = async (reqId) => {
    try {
      await api.post(`/help-requests/${reqId}/fulfill`);
      toast.success("Thank you for helping 💙");
      load();
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const shown = tab === "all" ? items : items.filter((i) => i.kind === tab);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl md:text-5xl text-[#1A202C]">Help Board</h1>
          <p className="text-[#4A5568] mt-2">Ask for support or offer a hand — thanked with AMIR.</p>
        </div>
        {user ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="new-help-btn" className="inline-flex items-center gap-2 bg-[#D4AF37] text-white hover:bg-[#B8972E] rounded-full px-6 py-3 font-heading font-semibold transition-colors shadow-sm">
                <Plus size={18} /> New post
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle className="font-heading">Post to the help board</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="flex gap-2">
                  {["request", "offer"].map((k) => (
                    <button key={k} data-testid={`help-kind-${k}`} onClick={() => setForm({ ...form, kind: k })}
                      className={`flex-1 py-2 rounded-xl font-heading text-sm capitalize transition-colors ${form.kind === k ? "bg-[#6B8EAC] text-white" : "bg-white border border-[#6A0DAD] text-[#4A5568]"}`}>
                      {k === "request" ? "I need help" : "I can help"}
                    </button>
                  ))}
                </div>
                <input data-testid="help-title-input" placeholder="Title (e.g. Need trach supplies)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC]" />
                <textarea data-testid="help-desc-input" placeholder="Describe what's needed…" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC]" />
                <label className="block">
                  <span className="text-sm font-heading text-[#4A5568]">AMIR offered as thanks (optional)</span>
                  <input data-testid="help-amir-input" type="number" min="0" value={form.amir_amount} onChange={(e) => setForm({ ...form, amir_amount: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC] mt-1" />
                </label>
              </div>
              <DialogFooter>
                <button data-testid="help-submit-btn" onClick={create} className="bg-[#D4AF37] text-white hover:bg-[#B8972E] rounded-full px-6 py-2.5 font-heading font-semibold transition-colors">Post</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : (
          <Link to="/auth" className="bg-[#D4AF37] text-white rounded-full px-6 py-3 font-heading font-semibold">Sign in to post</Link>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        {[["all", "All"], ["request", "Needs help"], ["offer", "Offering help"]].map(([k, label]) => (
          <button key={k} data-testid={`help-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-1.5 rounded-full text-sm font-heading transition-colors ${tab === k ? "bg-[#6B8EAC] text-white" : "bg-white border border-[#6A0DAD] text-[#4A5568]"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {shown.length === 0 && (
          <div className="md:col-span-2 bg-white border border-[#6A0DAD] rounded-2xl p-12 text-center text-[#4A5568]">
            Nothing here yet. Be the first to reach out.
          </div>
        )}
        {shown.map((i) => (
          <div key={i._id} data-testid={`help-card-${i._id}`} className="bg-white border border-[#6A0DAD] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-3">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-heading font-semibold ${i.kind === "request" ? "bg-[#6B8EAC]/12 text-[#5A7B96]" : "bg-[#D4AF37]/12 text-[#B8972E]"}`}>
                <HandHeart size={13} /> {i.kind === "request" ? "Needs help" : "Offering help"}
              </span>
              {i.amir_amount > 0 && (
                <span className="inline-flex items-center gap-1 text-[#B8972E] font-heading font-semibold text-sm">
                  <Coins size={14} /> {i.amir_amount} AMIR
                </span>
              )}
            </div>
            <h3 className="font-heading text-lg font-semibold text-[#1A202C]">{i.title}</h3>
            <p className="text-[#4A5568] mt-1.5 leading-relaxed">{i.description}</p>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#6A0DAD]">
              <p className="text-xs text-[#4A5568] font-heading">by {i.author_name}</p>
              {i.status === "fulfilled" ? (
                <span className="inline-flex items-center gap-1.5 text-green-600 font-heading text-sm font-semibold">
                  <CheckCircle2 size={16} /> Resolved{i.fulfilled_by_name ? ` by ${i.fulfilled_by_name}` : ""}
                </span>
              ) : user && i.author_id !== user._id ? (
                <button data-testid={`fulfill-btn-${i._id}`} onClick={() => fulfill(i._id)}
                  className="bg-[#6B8EAC] text-white hover:bg-[#5A7B96] rounded-full px-4 py-1.5 text-sm font-heading font-semibold transition-colors">
                  {i.kind === "request" ? "I'll help" : "Take offer"}
                </button>
              ) : (
                <span className="text-xs text-[#4A5568] font-heading">Open</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
